/** @jsxImportSource @emotion/react */
import { Autocomplete, FilterOptionsState, Paper, TextField } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useCallback, useEffect } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import * as turf from '@turf/turf'

import { handleRemoveRegionMasksLayers } from '@app/contexts/ModuleMapContexts/utils/layers'
import { useDebounce } from '@app/hooks/useDebounce'
import { useFetchRegions, type Region } from '@app/hooks/useFetchRegions'
import { regionFiltersAtom, regionMasksAtom } from '@app/risk-migration/atoms/riskFilters.atoms'
import { riskMapInstanceAtom } from '@app/risk-migration/atoms/riskMap.atoms'
import { useRiskMapNavigation } from '@app/risk-migration/hooks/useRiskMapNavigation'
import { autocompleteContainer, groupHeader, groupItems } from './RegionFilter.styles'

export type RegionOption = Region & {
  default?: boolean
}

const EMPTY_REGIONS: Region[] = []

// stable "nothing selected" value to fix MUI autocomplete reset
const NO_REGIONS_SELECTED: RegionOption[] = []

export const RegionFilter = () => {
  const map = useAtomValue(riskMapInstanceAtom)
  const regionMasks = useAtomValue(regionMasksAtom)
  const setRegionMasks = useSetAtom(regionMasksAtom)
  const regionFilters = useAtomValue(regionFiltersAtom)
  const setRegionFilters = useSetAtom(regionFiltersAtom)
  const { fitToBounds } = useRiskMapNavigation()
  const theme = useTheme()
  const debouncedRegionList = useDebounce(regionFilters, 100)

  useEffect(() => {
    // noop if we don't have any current regions or the map doesn't exist:
    if (!map || debouncedRegionList?.length == 0 || !regionFilters) return

    const boundingBoxes = debouncedRegionList?.map(
      ({ min_x, min_y, max_x, max_y }) =>
        [max_x, max_y, min_x, min_y] as [number, number, number, number],
    )

    const bbox = turf.bbox(
      turf.points(
        boundingBoxes?.flatMap((minmax) => [
          [minmax[0], minmax[1]],
          [minmax[2], minmax[3]],
        ]),
      ),
    )

    setRegionMasks(debouncedRegionList)

    fitToBounds({ boundingBox: [bbox[0], bbox[1], bbox[2], bbox[3]] })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedRegionList])

  const clientName = localStorage.getItem('client_name')
  const { data: regionsData = EMPTY_REGIONS } = useFetchRegions()

  const handleRemoveRegion = useCallback(() => {
    if (!map) return
    handleRemoveRegionMasksLayers({ map })
  }, [map])

  const handleChangeRegions = useCallback(
    (regions: RegionOption[] | null) => {
      if (!regions || regions.length === 0) {
        handleRemoveRegion()
        setRegionFilters(null)
        setRegionMasks(null)
        return
      }

      setRegionFilters(regions)
    },
    [handleRemoveRegion, setRegionFilters, setRegionMasks],
  )

  const GroupHeader = (props: React.HTMLAttributes<HTMLElement>) => <div {...props} />

  // TS is having a tiff with react portal vs react elements, it's seemingly non-sensical
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const GroupItems = (props: any) => <ul {...props} />

  useEffect(() => {
    if (!clientName) {
      handleRemoveRegion()
    }
  }, [clientName, handleRemoveRegion])

  if (!clientName || !map || !regionsData?.length || regionsData.length == 1) return null

  const filterSA1Regions = (options: RegionOption[], state: FilterOptionsState<RegionOption>) => {
    if (
      state.inputValue === '' &&
      options.some((option) => option.group_name == 'Statistical Area 1')
    ) {
      const newOptions = options.filter((option) => option.group_name != 'Statistical Area 1')

      // Add a dummy region just to show a message
      newOptions.unshift({
        group_name: 'Statistical Area 1',
        region_name: 'Please type in SA1 code above',
        region: '',
        default: false,
        min_x: 0,
        max_x: 0,
        min_y: 0,
        max_y: 0,
      })

      return newOptions
    }

    return options.filter((option) =>
      option.region_name.toLocaleLowerCase().includes(state.inputValue.toLocaleLowerCase()),
    )
  }

  return (
    <Paper>
      <Autocomplete
        sx={{ width: '100%', minWidth: '240px', height: '100%' }}
        css={autocompleteContainer({ theme })}
        value={regionMasks ?? NO_REGIONS_SELECTED}
        isOptionEqualToValue={(option, value) => option.region === value.region}
        size="small"
        fullWidth
        multiple
        disableCloseOnSelect
        limitTags={1}
        options={regionsData}
        groupBy={(option) => option.group_name}
        getOptionLabel={(option) => option.region_name}
        getOptionDisabled={(option) => option.group_name == 'Statistical Area 1'}
        onChange={(_event, values) => handleChangeRegions(values)}
        renderInput={(params) => (
          <TextField
            {...params}
            className="tutorial-region-filter"
            placeholder={regionMasks === null || regionMasks.length == 0 ? 'Filter By Regions' : ''}
          />
        )}
        renderGroup={(params) => (
          <li key={params.key}>
            <GroupHeader css={groupHeader({ theme })}>{params.group}</GroupHeader>
            <GroupItems css={groupItems({ theme })}>{params.children}</GroupItems>
          </li>
        )}
        filterOptions={filterSA1Regions}
      />
    </Paper>
  )
}
